import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  input,
  OnChanges,
  OnInit,
  signal,
  SimpleChanges,
  TemplateRef,
  WritableSignal,
} from '@angular/core';
import { Validators } from '@angular/forms';
import {
  ControlsOf,
  diff,
  FormControl,
  FormGroup,
} from '@ngneat/reactive-forms';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ModalRef, ModalService } from '@vinnova/ui';
import { InvalidXMLRegex, ValidateSpace } from '@vinnova/validation';
import {
  BehaviorSubject,
  distinctUntilChanged,
  filter,
  merge,
  Observable,
  of,
  skip,
  Subscription,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';
import { FormQuestionStandard } from '../../../core/models/form-group-mapping.model';
import { ReadonlyState } from '../../../core/models/form.model';
import { QuestionStructureTextarea } from '../../../core/models/question/question-textarea.model';
import { Question } from '../../../core/models/question/question.model';
import { RendererFormsQuery } from '../../../core/queries/renderer-forms.query';
import { BaseTranslationService } from '../../../core/services/base-translation.service';
import { FormValidationService } from '../../../core/services/form-validation.service';
import { QuestionNumberService } from '../../../core/services/question-number.service';
import { QuestionValueChangeService } from '../../../core/services/question-value-change.service';
import { TranslatedMaxLength } from './validators/translated-maxlength.validator';
import { PreviousAnswersService } from '../../../core/services/previous-answers.service';

@UntilDestroy()
@Component({
  selector: 'lib-question-textarea',
  templateUrl: './question-textarea.component.html',
  styleUrls: ['./question-textarea.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class QuestionTextareaComponent
  implements OnInit, AfterViewChecked, OnChanges
{
  value: FormControl<string>;
  questionNumber$: Observable<unknown>;
  readOnly$: Observable<ReadonlyState>;
  valueSwedish = new FormControl<string>('');
  valueEnglish = new FormControl<string>('');
  isLoading: boolean;
  translated = false;
  confirmTranslateModal: ModalRef;
  translatedHelpTextShow = false;
  translatedByMachineHeader = '<This text has been machine translated>\n';
  styleFirstLine: boolean;
  englishMaxLength: number;
  addedTranslatedByMachineTextLength = false;
  activeLang$: Observable<string>;
  errorMessage$: Observable<string>;
  errorMessageSwe$: Observable<string>;
  errorMessageEng$: Observable<string>;
  swedishHelpText$: Observable<string>;
  translateButtonClicked = new BehaviorSubject<boolean>(false);
  translateButtonClicked$ = this.translateButtonClicked.asObservable();
  size: 'small' | 'medium' | 'large';
  readonly: WritableSignal<string> = signal(undefined);
  sweReadonly: WritableSignal<string> = signal(undefined);
  engReadonly: WritableSignal<string> = signal(undefined);
  valueChangeSubscription: Subscription;
  patchValueSubscription: Subscription;
  showChanges = input<boolean>();
  readonly previousAnswersService = inject(PreviousAnswersService);

  @Input() labelPrefix: string;
  @Input() repeaterGroupId: string;
  @Input() question: Question;

  _questionForm: FormGroup<ControlsOf<FormQuestionStandard>>;
  @Input()
  set questionForm(form: FormGroup<ControlsOf<FormQuestionStandard>>) {
    this._questionForm = form;
    this.value = this._questionForm.get('value');
    this.value.touch$.pipe(take(1)).subscribe(() => {
      this.cdr.markForCheck();
    });
  }

  get questionForm() {
    return this._questionForm;
  }

  get structure() {
    return this.question?.structure as QuestionStructureTextarea;
  }

  get translateOption(): boolean {
    return this.structure.translateOption;
  }

  constructor(
    private formValidationService: FormValidationService,
    private cdr: ChangeDetectorRef,
    private questionValueChangeService: QuestionValueChangeService,
    private translocoService: TranslocoService,
    private questionNumberService: QuestionNumberService,
    private translationService: BaseTranslationService,
    private modalService: ModalService,
    private formsQuery: RendererFormsQuery,
  ) {}

  ngOnInit() {
    this.readOnly$ = this.formsQuery.selectReadOnly();
    this.activeLang$ = this.translocoService.langChanges$;

    // populate initial answers from the db
    this.questionValueChangeService.patchInitialValue(
      this.question.id,
      this.repeaterGroupId,
      this.value,
      this.question,
    );

    this.questionNumber$ = this.questionNumberService.getQuestionNumberForLabel(
      this.question.id,
    );

    if (this.structure.translateOption) {
      this.value.clearValidators();
      const parsed = this.setSweEngValue();

      if (parsed?.valueSwedish) {
        this.valueSwedish.markAsTouched();
        this.valueSwedish.markAsDirty();
      }
      if (parsed?.valueEnglish) {
        this.valueEnglish.markAsTouched();
        this.valueEnglish.markAsDirty();
      }

      if (this.question.validation.required) {
        this.valueSwedish.addValidators([Validators.required, ValidateSpace]);
        this.valueEnglish.addValidators([Validators.required, ValidateSpace]);
      }
      const maxLength = this.question?.validation.maxLength;
      if (maxLength) {
        this.valueSwedish.addValidators([Validators.maxLength(maxLength)]);
        this.valueEnglish.addValidators(
          TranslatedMaxLength(maxLength, this.translatedByMachineHeader),
        );
        this.setEnglishTextMaxLength();
      }
      this._questionForm.addControl('valueSwedish', this.valueSwedish);
      this._questionForm.addControl('valueEnglish', this.valueEnglish);

      this.swedishHelpText$ = merge(
        this.valueSwedish.value$.pipe(diff()),
        this.translateButtonClicked$.pipe(distinctUntilChanged()),
      ).pipe(
        distinctUntilChanged(),
        switchMap(() => {
          return !this.question.validation.required &&
            this.translateButtonClicked.value &&
            !this.valueSwedish.value
            ? this.translocoService.selectTranslateObject('required')
            : of(undefined);
        }),
      );
    }
    this.readOnly$.pipe(untilDestroyed(this)).subscribe((readonly) => {
      if (readonly) {
        this.setReadonly();
      }
      if (!readonly && this.valueSwedish && this.valueEnglish) {
        this.formControlValueChanges(this.valueSwedish);
        this.formControlValueChanges(this.valueEnglish);
      }
    });

    this.setErrorMessage();

    if (
      this.question.validation.maxLength &&
      this.question.validation.maxLength < 500
    ) {
      this.size = 'small';
    } else if (
      !this.question.validation.maxLength ||
      this.question.validation.maxLength < 1000
    ) {
      this.size = 'medium';
    } else {
      this.size = 'large';
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['repeaterGroupId']) {
      if (this.valueChangeSubscription) {
        this.valueChangeSubscription.unsubscribe();
      }
      if (this.patchValueSubscription) {
        this.valueChangeSubscription.unsubscribe();
      }

      this.valueChangeSubscription = this.questionValueChangeService
        .valueChangeListener(
          this.value,
          {
            questionId: this.question.id,
            repeaterGroupId: this.repeaterGroupId,
          },
          this.question,
        )
        .pipe(untilDestroyed(this))
        .subscribe();

      this.patchValueSubscription = this.questionValueChangeService
        .patchValueListener(this.question, this.repeaterGroupId, this.value)
        .pipe(untilDestroyed(this))
        .subscribe(() => this.setReadonly());

      if (
        this.formsQuery.getReadOnly() &&
        !changes['repeaterGroupId'].firstChange
      ) {
        this.setReadonly();
      }
    }
  }

  setErrorMessage() {
    if (this.structure.translateOption) {
      this.errorMessageSwe$ = this.readOnly$.pipe(
        switchMap((readonly) => {
          switch (readonly) {
            case ReadonlyState.Editable: {
              return this.formValidationService
                .questionInvalid(this.valueSwedish, this.question.id)
                .pipe(
                  switchMap((invalid) =>
                    this.errorMessage(invalid, this.valueSwedish, false),
                  ),
                );
            }
            case ReadonlyState.Locked: {
              return this.errorMessage(true, this.valueSwedish, readonly);
            }
            case ReadonlyState.Reader: {
              return of(undefined);
            }
          }
        }),
      );

      this.errorMessageEng$ = this.readOnly$.pipe(
        switchMap((readonly) => {
          switch (readonly) {
            case ReadonlyState.Editable: {
              return this.formValidationService
                .questionInvalid(this.valueEnglish, this.question.id)
                .pipe(
                  switchMap((invalid) =>
                    this.errorMessage(invalid, this.valueEnglish, false),
                  ),
                );
            }
            case ReadonlyState.Locked: {
              return this.errorMessage(true, this.valueEnglish, readonly);
            }
            case ReadonlyState.Reader: {
              return of(undefined);
            }
          }
        }),
      );
    } else {
      this.errorMessage$ = this.readOnly$.pipe(
        switchMap((readonly) => {
          switch (readonly) {
            case ReadonlyState.Editable: {
              return this.formValidationService
                .questionInvalid(this.value, this.question.id)
                .pipe(
                  switchMap((invalid) =>
                    this.errorMessage(invalid, this.value, false),
                  ),
                );
            }
            case ReadonlyState.Locked: {
              return this.errorMessage(true, this.value, readonly);
            }
            case ReadonlyState.Reader: {
              return of(undefined);
            }
          }
        }),
      );
    }
  }

  errorMessage(
    invalid: boolean,
    formControl: FormControl<string>,
    isReadonly,
  ): Observable<string> {
    if (invalid) {
      if (formControl.errors?.required) {
        return isReadonly
          ? this.translocoService.selectTranslate('field_required')
          : this.translocoService.selectTranslate('required');
      }

      if (
        formControl.errors?.maxlength ||
        formControl.errors?.TranslatedMaxLength
      ) {
        return this.translocoService.selectTranslate('validation_maxlength');
      }
    }

    return of(undefined);
  }

  formControlValueChanges(formControl: FormControl<string>) {
    formControl.valueChanges
      .pipe(
        skip(1),
        untilDestroyed(this),
        takeUntil(
          this.readOnly$.pipe(filter((a) => a !== ReadonlyState.Editable)),
        ),
      )
      .subscribe(() => {
        this.styleFirstLine = this.valueEnglish.value?.startsWith(
          this.translatedByMachineHeader,
        );
        this.setEnglishTextMaxLength();
        if (this.valueEnglish.value === '') {
          this.translatedHelpTextShow = false;
        }
        if (this.valueSwedish.value !== '') {
          this.translateButtonClicked.next(false);
        }
        this.value.patchValue(
          !this.valueSwedish.value && !this.valueEnglish.value
            ? undefined
            : JSON.stringify({
                valueSwedish: this.valueSwedish.value?.replaceAll(
                  InvalidXMLRegex,
                  '',
                ),
                valueEnglish: this.valueEnglish.value?.replaceAll(
                  InvalidXMLRegex,
                  '',
                ),
              }),
        );
        this.value.markAsDirty();
        this.cdr.detectChanges();
      });
  }

  private setEnglishTextMaxLength() {
    if (this.question?.validation?.maxLength) {
      this.englishMaxLength = this.valueEnglish.value?.startsWith(
        this.translatedByMachineHeader,
      )
        ? this.question.validation.maxLength +
          this.translatedByMachineHeader.length
        : this.question.validation.maxLength;
    }
  }

  translateModal(content: TemplateRef<unknown>) {
    this.translateButtonClicked.next(true);
    this.addedTranslatedByMachineTextLength = false;
    if (this.valueSwedish.value) {
      this.confirmTranslateModal = this.modalService.show(content, {
        backdrop: 'static',
      });
    } else {
      this.valueSwedish.markAsDirty();
    }
  }

  swedishHelpText(): string {
    if (
      !this.question.validation.required &&
      this.translateButtonClicked.value &&
      !this.valueSwedish.value
    ) {
      return this.translocoService.translate('required');
    }
  }

  translateText() {
    this.isLoading = true;
    this.translationService
      .translateText(this.valueSwedish.value)
      .pipe(untilDestroyed(this))
      .subscribe((val) => {
        this.valueEnglish.markAsDirty();
        this.isLoading = false;
        this.translated = true;
        this.translatedHelpTextShow = true;
        this.valueEnglish.patchValue(this.translatedByMachineHeader + val);
      });
  }

  translateToFalse() {
    this.translated = false;
  }

  ngAfterViewChecked() {
    this.cdr.markForCheck();
  }

  private setReadonly() {
    if (this.structure.translateOption) {
      this.setSweEngValue();
      this.sweReadonly.set(this.valueSwedish.value);
      this.engReadonly.set(this.valueEnglish.value);
    } else {
      this.readonly.set(this.value.value);
    }
  }

  private setSweEngValue() {
    const parsed = this.value?.value
      ? JSON.parse(this.value.value)
      : { valueSwedish: undefined, valueEnglish: undefined };
    this.valueSwedish.patchValue(parsed?.valueSwedish);
    this.valueEnglish.patchValue(parsed?.valueEnglish);
    return parsed;
  }
}
